import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/bi/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { isTenantAdmin } from "@/lib/roles";
import { formatNumber } from "@/lib/chartTheme";
import { useNavigate } from "react-router-dom";
import { BadgeCheck, Plus, Search, Trash2, Loader2, TrendingUp, Database, Gem } from "lucide-react";

function Sparkline({ points }) {
  if (!points?.length) return null;
  const vals = points.map((p) => Number(p.value) || 0);
  const min = Math.min(...vals), max = Math.max(...vals);
  const norm = (v) => max === min ? 12 : 22 - ((v - min) / (max - min)) * 20;
  const d = vals.map((v, i) => `${(i / Math.max(vals.length - 1, 1)) * 100},${norm(v)}`).join(" ");
  const up = vals[vals.length - 1] >= vals[0];
  return (
    <svg viewBox="0 0 100 24" className="w-full h-6" preserveAspectRatio="none">
      <polyline points={d} fill="none" stroke={up ? "#16A34A" : "#DC2626"} strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function MetricCard({ m, canManage, onDelete, onCertify }) {
  const [live, setLive] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    let ok = true;
    api.get(`/metrics/${m.id}/value`).then(({ data }) => ok && setLive(data)).catch(() => ok && setLive({ error: true }));
    return () => { ok = false; };
  }, [m.id]);
  return (
    <div data-testid={`metric-card-${m.id}`} className="glass-card p-4 space-y-2 flex flex-col">
      <div className="flex items-start gap-2">
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-1.5">
            <h3 className="text-sm font-semibold text-[color:var(--ink)] truncate">{m.name}</h3>
            {m.certified && <BadgeCheck className="w-4 h-4 text-[color:var(--brand-ocean)] shrink-0" title="Certified metric" />}
          </span>
          <span className="text-[11px] text-[color:var(--ink-3)] block truncate">{m.measure_label} · {m.dataset_name}</span>
        </span>
        {canManage && (
          <span className="flex items-center gap-1">
            <button data-testid={`metric-certify-${m.id}`} title={m.certified ? "Remove certification" : "Certify"} onClick={() => onCertify(m)}
              className={m.certified ? "text-[color:var(--brand-ocean)]" : "text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]"}><BadgeCheck className="w-4 h-4" /></button>
            <button data-testid={`metric-delete-${m.id}`} title="Delete" onClick={() => onDelete(m)} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><Trash2 className="w-4 h-4" /></button>
          </span>
        )}
      </div>
      {m.description && <p className="text-xs text-[color:var(--ink-2)] line-clamp-2">{m.description}</p>}
      <div className="flex items-end gap-3 mt-auto">
        <span data-testid={`metric-value-${m.id}`} className="text-2xl font-bold font-display text-[color:var(--ink)]">
          {live == null ? <Loader2 className="w-5 h-5 animate-spin text-[color:var(--ink-3)]" />
            : live.error ? <span className="text-sm text-[color:var(--danger)]">unavailable</span>
              : formatNumber(Number(live.value))}
        </span>
        <span className="flex-1">{live?.trend?.length > 1 && <Sparkline points={live.trend} />}</span>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {(m.tags || []).map((t) => <Badge key={t} variant="outline" className="text-[10px] text-[color:var(--ink-3)]">{t}</Badge>)}
        <span className="flex-1" />
        <button data-testid={`metric-open-model-${m.id}`} onClick={() => nav(`/model/${m.dataset_id}`)}
          className="text-[10px] text-[color:var(--brand-ocean)] hover:underline flex items-center gap-1"><Database className="w-3 h-3" />model</button>
      </div>
    </div>
  );
}

export default function Metrics() {
  const { user } = useAuth();
  const admin = isTenantAdmin(user?.role);
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [certOnly, setCertOnly] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const load = () => api.get("/metrics", { params: { q, certified_only: certOnly } }).then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, certOnly]);

  const del = async (m) => {
    try { await api.delete(`/metrics/${m.id}`); toast.success(`Metric "${m.name}" deleted`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };
  const certify = async (m) => {
    try { await api.patch(`/metrics/${m.id}`, { certified: !m.certified }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Only admins can certify"); }
  };

  return (
    <AppShell>
      <div className="p-6 max-w-6xl mx-auto" data-testid="metrics-page">
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div>
            <h1 className="font-display text-2xl font-bold text-[color:var(--ink)] flex items-center gap-2"><Gem className="w-6 h-6 text-[color:var(--brand-ocean)]" />Metrics catalog</h1>
            <p className="text-sm text-[color:var(--ink-3)]">One source of truth for your business metrics — certified, governed, live.</p>
          </div>
          <span className="flex-1" />
          <Button data-testid="metric-add-btn" onClick={() => setAddOpen(true)} className="bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white">
            <Plus className="w-4 h-4 mr-1" />Add metric
          </Button>
        </div>
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative max-w-xs flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[color:var(--ink-3)]" />
            <Input data-testid="metrics-search" className="pl-8 bg-[color:var(--surface)]" placeholder="Search metrics…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-xs text-[color:var(--ink-2)]">
            <Switch data-testid="metrics-certified-only" checked={certOnly} onCheckedChange={setCertOnly} />
            <BadgeCheck className="w-4 h-4 text-[color:var(--brand-ocean)]" />Certified only
          </label>
        </div>
        {items == null ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--brand-ocean)]" /></div>
        ) : items.length === 0 ? (
          <div className="glass-card p-10 text-center">
            <TrendingUp className="w-8 h-8 mx-auto text-[color:var(--ink-3)] mb-2" />
            <p className="text-sm text-[color:var(--ink-2)] font-medium">No metrics in the catalog yet</p>
            <p className="text-xs text-[color:var(--ink-3)] mt-1">Promote a governed measure (e.g. Total Sales) so everyone uses the same definition.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((m) => <MetricCard key={m.id} m={m} canManage={admin || m.owner_id === user?.id} onDelete={del} onCertify={certify} />)}
          </div>
        )}
        <AddMetricDialog open={addOpen} onOpenChange={setAddOpen} onDone={load} admin={admin} />
      </div>
    </AppShell>
  );
}

function AddMetricDialog({ open, onOpenChange, onDone, admin }) {
  const [datasets, setDatasets] = useState([]);
  const [dsId, setDsId] = useState("");
  const [measures, setMeasures] = useState([]);
  const [measureKey, setMeasureKey] = useState("");
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [tags, setTags] = useState("");
  const [certified, setCertified] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/datasets").then(({ data }) => setDatasets(data || [])).catch(() => {});
    setDsId(""); setMeasures([]); setMeasureKey(""); setName(""); setDesc(""); setTags(""); setCertified(false);
  }, [open]);
  useEffect(() => {
    if (!dsId) { setMeasures([]); return; }
    api.get(`/datasets/${dsId}/model`).then(({ data }) => setMeasures(data?.measures || [])).catch(() => setMeasures([]));
  }, [dsId]);

  const selMeasure = useMemo(() => measures.find((x) => x.key === measureKey), [measures, measureKey]);
  useEffect(() => { if (selMeasure && !name) setName(selMeasure.label || selMeasure.key); /* eslint-disable-next-line */ }, [selMeasure]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/metrics", {
        name: name.trim(), description: desc.trim(), dataset_id: dsId, measure_key: measureKey,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean), certified,
      });
      toast.success(`Metric "${name.trim()}" added to the catalog`);
      onOpenChange(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create the metric"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="metric-add-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Gem className="w-5 h-5 text-[color:var(--brand-ocean)]" />Add metric</DialogTitle>
          <DialogDescription>Point at a governed measure — the catalog value always follows the model definition.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Dataset</Label>
            <Select value={dsId} onValueChange={setDsId}>
              <SelectTrigger data-testid="metric-dataset-select" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue placeholder="Pick a dataset" /></SelectTrigger>
              <SelectContent className="max-h-60">{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Governed measure</Label>
            <Select value={measureKey} onValueChange={setMeasureKey} disabled={!dsId}>
              <SelectTrigger data-testid="metric-measure-select" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue placeholder={dsId ? "Pick a measure" : "Pick a dataset first"} /></SelectTrigger>
              <SelectContent className="max-h-60">{measures.map((x) => <SelectItem key={x.key} value={x.key}>{x.label || x.key}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Name</Label><Input data-testid="metric-name-input" className="mt-1.5 bg-[color:var(--surface)]" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Total Revenue" /></div>
          <div><Label className="text-xs">Description (business definition)</Label><Input data-testid="metric-desc-input" className="mt-1.5 bg-[color:var(--surface)]" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What this metric means and how it's used" /></div>
          <div><Label className="text-xs">Tags (comma-separated)</Label><Input data-testid="metric-tags-input" className="mt-1.5 bg-[color:var(--surface)]" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="finance, sales" /></div>
          {admin && (
            <label className="flex items-center gap-2 text-sm text-[color:var(--ink-2)]">
              <Switch data-testid="metric-certified-switch" checked={certified} onCheckedChange={setCertified} />
              <BadgeCheck className="w-4 h-4 text-[color:var(--brand-ocean)]" />Certified (approved company-wide definition)
            </label>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="metric-save-btn" onClick={save} disabled={busy || !dsId || !measureKey || !name.trim()} className="bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}Add to catalog
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
